import { inject, Injectable } from '@angular/core'
import { CourtSystem } from '@ftr/contracts/api/court-system'
import { Region } from '@ftr/contracts/type/court'
import { Uuid } from '@ftr/contracts/type/shared'
import { unwrapData } from '@ftr/foundation'
import { from, map, Observable, of, switchMap, tap } from 'rxjs'
import { GetCourtSystemService } from './get-court-system.service'

/**
 * Request a court-system by id if it does not yet exist in the cache
 */
@Injectable({
  providedIn: 'root',
})
export class CourtSystemRegionCache {
  private readonly getCourtSystemService = inject(GetCourtSystemService)

  private courtSystems: CourtSystem[] = []
  private regionByCode = new Map<string, Region>()

  getOrFetchCourtSystem(courtSystemId$: Observable<Uuid>): Observable<CourtSystem> {
    return courtSystemId$.pipe(
      switchMap(courtSystemId => {
        const courtInCache = this.courtSystems.find(court => court.id === courtSystemId)
        if (courtInCache) {
          return from([courtInCache])
        }

        return this.requestAndCache(courtSystemId)
      }),
    )
  }

  getOrFetchRegionForCourtSystemCode(code: string): Observable<Region> {
    const cachedRegion = this.regionByCode.get(code)
    if (cachedRegion) {
      return of(cachedRegion)
    }

    return this.getCourtSystemService.getRegionForCourtSystemCode(code).pipe(
      unwrapData(),
      map(response => response.region),
      tap(region => this.regionByCode.set(code, region)),
    )
  }

  cacheRegionForCode(code: string, region: Region): void {
    this.regionByCode.set(code, region)
  }

  private requestAndCache(courtSystemId: Uuid): Observable<CourtSystem> {
    return this.getCourtSystemService.getCourtSystem(courtSystemId).pipe(
      unwrapData(),
      tap(courtSystem => this.courtSystems.push(courtSystem)),
    )
  }
}
